import React from 'react'

import { ThemeProvider } from '@fe-toolkit/web-components/ssr'

import getConfig from 'next/config'
import { useRouter } from 'next/router'

import { StyleSheetManager } from 'styled-components'

import { IntercomMessenger } from '@giffgaff/broadband-common'

import GlobalStyle from '@/styles/global.styled'
import { AuthProvider } from '@/context/AuthContext'
import { ErrorProvider, UIFlagsProvider } from '@/context'
import { useMemberContext } from '@/context/MemberContext'
import QueryClientProvider from '@/api/QueryClientProvider'
import { useGetIntercomMember } from '@/hooks/apiHooks'

import MainLayout from '@/layouts/Main'

import HydrationGuard from '@/components/HydrationGuard'
import ErrorBoundary from '@/components/ErrorBoundary'

import MemberProviderWrapper from './MemberProviderWrapper'
import CTCProviderWrapper from './CTCProvider'

const { publicRuntimeConfig } = getConfig()

type ProviderProps = { children: React.ReactNode }

// MainLayout takes the member's name as a prop rather than reading MemberContext itself, so it
// stays a plain layout component - MemberContext.tsx's own loading state also renders MainLayout,
// and having MainLayout depend on MemberContext would make that a circular import.
export const MainLayoutWithMember = ({ children }: ProviderProps) => {
  const { data } = useMemberContext()

  return <MainLayout username={data?.data?.memberName}>{children}</MainLayout>
}

const Providers = ({ children }: ProviderProps) => {
  const router = useRouter()

  return (
    <ThemeProvider>
      <GlobalStyle />
      <StyleSheetManager>
        <ErrorBoundary resetKey={router.asPath}>
          <HydrationGuard>
            <ErrorProvider>
              <QueryClientProvider>
                <AuthProvider>
                  <MemberProviderWrapper>
                    <UIFlagsProvider>
                      <CTCProviderWrapper>
                        <MainLayoutWithMember>{children}</MainLayoutWithMember>

                        {publicRuntimeConfig.INTERCOM_APP_ID && (
                          <IntercomMessenger
                            appId={publicRuntimeConfig.INTERCOM_APP_ID}
                            useGetIntercomMember={useGetIntercomMember}
                          />
                        )}
                      </CTCProviderWrapper>
                    </UIFlagsProvider>
                  </MemberProviderWrapper>
                </AuthProvider>
              </QueryClientProvider>
            </ErrorProvider>
          </HydrationGuard>
        </ErrorBoundary>
      </StyleSheetManager>
    </ThemeProvider>
  )
}

export default Providers
